import React, { useState, forwardRef, useImperativeHandle } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";

const styles = {
  infoText: {
    fontWeight: "300",
    margin: "10px 0 30px",
    textAlign: "center",
  },
  errorText: {
    color: "red",
  },
};

const useStyles = makeStyles(styles);

const Step3 = forwardRef((props, ref) => {
  const classes = useStyles();

  const [minAge, setMinAge] = useState(18);
  const [maxAge, setMaxAge] = useState(""); // ✅ now blank by default
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const minAgeOptions = [...Array(73).keys()].map((i) => i + 18);
  const maxAgeOptions = [...Array(91 - minAge).keys()].map(
    (i) => i + minAge + 1
  );

  const validateFields = () => {
    const newErrors = {};

    if (!maxAge) newErrors.maxAge = "Please select a maximum age";
    else if (maxAge <= minAge)
      newErrors.maxAge = "Maximum age must be greater than minimum age";

    if (!password || password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (confirmPassword !== password)
      newErrors.confirmPassword = "Passwords do not match";

    if (!terms) newErrors.terms = "You must agree to the Terms and Conditions";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    sendState: () => {
      if (validateFields()) {
        return { minAge, maxAge, password, confirmPassword, terms };
      }
      return null;
    },
    validateBeforeNext: validateFields,
  }));

  return (
    <GridContainer justify="center">
      <GridItem xs={12}>
        <h4 className={classes.infoText}>Your Preferences and Security</h4>
      </GridItem>

      {/* Seeking Age */}
      <GridItem xs={12}>
        <h5>Seeking Age Range</h5>
      </GridItem>

      {/* Min Age */}
      <GridItem xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Minimum Age</InputLabel>
          <Select
            value={minAge}
            onChange={(e) => {
              const newMinAge = parseInt(e.target.value);
              setMinAge(newMinAge);

              if (maxAge && maxAge <= newMinAge) {
                setMaxAge("");
              }
            }}
          >
            {minAgeOptions.map((age) => (
              <MenuItem key={age} value={age}>
                {age}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </GridItem>

      {/* Max Age */}
      <GridItem xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.maxAge}>
          <InputLabel id="max-age-label">Maximum Age</InputLabel>

          <Select
            labelId="max-age-label"
            value={maxAge}
            onChange={(e) => setMaxAge(parseInt(e.target.value))}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "#999" }}></span>;
              }
              return selected;
            }}
          >
            {maxAgeOptions.map((age) => (
              <MenuItem key={age} value={age}>
                {age}
              </MenuItem>
            ))}
          </Select>

          {errors.maxAge && <FormHelperText>{errors.maxAge}</FormHelperText>}
        </FormControl>
      </GridItem>

      {/* Password */}
      <GridItem xs={12} sm={6}>
        <CustomInput
          labelText="Password"
          id="password"
          formControlProps={{ fullWidth: true, error: !!errors.password }}
          inputProps={{
            type: showPassword ? "text" : "password",
            value: password,
            onChange: (e) => setPassword(e.target.value),
            endAdornment: (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </span>
            ),
          }}
        />
        {errors.password && (
          <p className={classes.errorText}>{errors.password}</p>
        )}
      </GridItem>

      {/* Confirm Password */}
      <GridItem xs={12} sm={6}>
        <CustomInput
          labelText="Confirm Password"
          id="confirm-password"
          formControlProps={{
            fullWidth: true,
            error: !!errors.confirmPassword,
          }}
          inputProps={{
            type: showConfirmPassword ? "text" : "password",
            value: confirmPassword,
            onChange: (e) => setConfirmPassword(e.target.value),
            endAdornment: (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </span>
            ),
          }}
        />
        {errors.confirmPassword && (
          <p className={classes.errorText}>{errors.confirmPassword}</p>
        )}
      </GridItem>

      {/* Terms */}
      <GridItem xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              color="primary"
            />
          }
          label={
            <span>
              By signing up for this service, I confirm that I am of legal adult
              age and agree to the{" "}
              <a
                href="/auth/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              ,{" "}
              <a
                href="/auth/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              , and{" "}
              <a
                href="/auth/affiliate-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Affiliate Terms
              </a>
              .
            </span>
          }
        />
        {errors.terms && <p className={classes.errorText}>{errors.terms}</p>}
      </GridItem>
    </GridContainer>
  );
});

Step3.displayName = "Step3";
export default Step3;
